import { Button } from 'antd';
import styles from './generatedStory.module.scss';
import { GoChevronRight } from 'react-icons/go';
import { IoIosRefresh, IoMdPlayCircle } from 'react-icons/io';
import { ChangeEvent, useRef, useState } from 'react';
import { IoPauseCircle } from 'react-icons/io5';
import { IWhatIfStory } from 'src/core/repo/whatif/types';

interface Props {
  userStoryText: string;
  isGenerating: boolean;
  setStep: (state: number) => void;
  handleRestRecording: () => void;
  audioUrl?: string;
  setUserStoryText?: (text: string) => void;
  story?: IWhatIfStory;
}

const GeneratedStory = ({
  userStoryText,
  isGenerating,
  setStep,
  handleRestRecording,
  audioUrl,
  setUserStoryText,
  story,
}: Props) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      setDuration(audioRef.current.duration);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const safeCurrentTime =
    !isNaN(currentTime) && isFinite(currentTime) ? currentTime : 0;
  const safeDuration = duration > 0 && isFinite(duration) ? duration : 0;

  return (
    <div className={styles.card_generated_story}>
      <div className={styles.card__header}>
        <h2>{story?.StoryTitle}</h2>
        <p>{story?.StoryText}</p>
        <h4>{story?.StoryWhatIf}</h4>
      </div>

      <div className={styles.generated_story_card}>
        <div className={styles.generated_story_card_actions}>
          <div className={styles.title}>
            <h5>Recording Complete</h5>
            <div className={styles.audio_player_wrapper}>
              <button
                className={styles.audio_player_btn}
                onClick={() => {
                  if (audioRef.current) {
                    if (isPlaying) {
                      audioRef.current.pause();
                    } else {
                      audioRef.current.play();
                    }
                  }
                }}>
                {isPlaying ? (
                  <IoPauseCircle size={28} color="#A020F0" />
                ) : (
                  <IoMdPlayCircle size={28} color="#A020F0" />
                )}
              </button>

              <div className={styles.audio_player_progress_wrapper}>
                <div className={styles.audio_player_progress_bg}>
                  <div
                    className={styles.audio_player_progress_fg}
                    style={{
                      width: `${
                        safeDuration > 0
                          ? (safeCurrentTime / safeDuration) * 100
                          : 0
                      }%`,
                    }}
                  />
                </div>
                <div className={styles.audio_player_times}>
                  <span>{formatTime(safeCurrentTime)}</span>
                  <span>
                    {safeDuration > 0 ? formatTime(safeDuration) : '00:00'}
                  </span>
                </div>
              </div>

              <audio
                ref={audioRef}
                src={audioUrl}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                style={{ display: 'none' }}
              />
            </div>
          </div>

          <div className={styles.actions}>
            <Button onClick={handleRestRecording}>
              <IoIosRefresh size={18} />
              <p>Try Again</p>
            </Button>
          </div>
        </div>

        <textarea
          value={userStoryText}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
            setUserStoryText?.(e.currentTarget.value ?? '')
          }
          className={styles.generated_story_text}
          // placeholder="Write or edit the story here..."
        />
      </div>
    </div>
  );
};

export default GeneratedStory;
